import {
  GoogleMap,
  TrafficLayerF,
  useJsApiLoader,
} from "@react-google-maps/api";
  
import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { isMobile } from "react-device-detect";
  
import { useAuth } from "../../../core/contexts/AuthContext";
import * as LocationsApi from "../../Common/Apis/LocationsApi";
import CustomMapControl from "./DisplayItem/CustomDrawingManagerControl";
import DisplayCircle from "./DisplayItem/DisplayCircle";
import DisplayInfoWindow from "./DisplayItem/DisplayInfoWindow";
import DisplayMarkerCluster from "./DisplayItem/DisplayMarkerCluster";
import DisplayMarkerWithLabel from "./DisplayItem/DisplayMarkerWithLabel";
import DisplayPolygon from "./DisplayItem/DisplayPolygon";
import DisplayPolyline from "./DisplayItem/DisplayPolyline";
import DrawingGeoBoundary from "./DisplayItem/DrawingGeoBoundary";
import SpotMarker from "./DisplayItem/SpotMarker";
import TripIconAnimation from "./DisplayItem/TripIconAnimation_2";
import moment from "moment";
  
//https://developers.google.com/maps/documentation/javascript/controls#ControlPositioning
const googleMapPosition = {
  TOP_LEFT: 1,
  TOP_CENTER: 2,
  TOP_RIGHT: 3,
  RIGHT_TOP: 7,
};
  
const LocationMap = ({
  zoom,
  locations,
  selectedVehicle,
  isCenter,
  isCluster = false,
  isUseMarkerLabel = false,
  selectedHistory,
  unSelectHistory,  
  isPolyline = false,
  isDrawing = false,
  isMarker = false,
  centerLatitude = -36.8597274,
  centerLongitude = 174.5414624,
  drawedGeoBoundary = null,
  drawingCompleted = true,
  spotMarker = null,
}) => {
  const { authTokens } = useAuth();
  const [selectedMarker, setSelectedMarker] = useState(null);
  const [geoBounds, setGeoBounds] = useState(null);
  const [tripHistory] = useState();
  const [, setShowTripMarker] = useState(false);
  const [mapCenter, setMapCenter] = useState({
    lat: centerLatitude,
    lng: centerLongitude,
  });
  const [isDrawingMode, setIsDrawingMode] = useState(false);
  const [isVehicleLabel] = useState(isUseMarkerLabel);
  const [_mapKey] = useState(moment());

  const { isLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: window.rucmonkeyGlobalConfigs.mapKey,
    preventGoogleFontsLoading: true,
    skipGoogle: window.google && window.google.maps ? true : false
  });
  const googleMapRef = useRef(null);
    
  // const bounds = new window.google.maps.LatLngBounds();
  const boundsRef = useRef(null);
  
  const [isTrafficLayer, setisTrafficLayer] = useState(false);
  
  const markersRef = useRef([]);
  
  // Track the user's preferred zoom
  const userZoomRef = useRef(null);
  
  // Add a new ref to track user interaction with the map
  const userHasMovedMapRef = useRef(false);
  
  const debounce = (func, wait) => {
    let timeout;
    return function(...args) {
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(this, args), wait);
    };
  };
    
  const debouncedFitBounds = useCallback(
    debounce((map, bounds) => {
      if (map && bounds) {
        map.fitBounds(bounds);

        const zoomAfterBoundsFit = googleMapRef.current.getZoom();
        if (zoomAfterBoundsFit > 16) {
          googleMapRef.current.setZoom(16);
        }
      }
    }, 300),
    []
  );
  
  const simplifyPath = (points, tolerance = 0.0001) => {
    if (!points || points.length <= 2) return points;
      
    // Simple distance-based filtering algorithm
    const simplified = [points[0]];
    let lastPoint = points[0];
      
    for (let i = 1; i < points.length - 1; i++) {
      const dx = points[i].lat - lastPoint.lat;
      const dy = points[i].lng - lastPoint.lng;
      const distance = Math.sqrt(dx*dx + dy*dy);
        
      if (distance > tolerance) {
        simplified.push(points[i]);
        lastPoint = points[i];
      }
    }
      
    // Always include the last point
    if (points.length > 1) {
      simplified.push(points[points.length - 1]);
    }
      
    return simplified;
  };
  
  
  useEffect(() => {
    if (isLoaded) { 
      // Create bounds after the Maps API is loaded
      // boundsRef = new window.google.maps.LatLngBounds();
      boundsRef.current = new window.google.maps.LatLngBounds();
    }
  
  }, [isLoaded]);
  
  useEffect(() => {
    if (!googleMapRef.current || !locations) return;
      
    // Always clear previous markers
    markersRef.current.forEach(marker => marker.setMap(null));
    markersRef.current = [];
      
  }, [locations, isCluster]);
  
  
  useEffect(() => { 
    if (googleMapRef.current && zoom) { 
      googleMapRef.current.setZoom(zoom); }
  }, [zoom]);
  
  
  function handleZoomChanged() {
    if (googleMapRef.current) {
      const currentZoom = googleMapRef.current.getZoom();
      userZoomRef.current = currentZoom;
  
      if (currentZoom >= 16) {
        setShowTripMarker(true);
      } else {
        setShowTripMarker(false);
      }
    }
  }
    
  useEffect(() => {
    /*
    console.log("GoogleMap_New.js: useEffect: selectedVehicle:", {
      isCluster,
      locations,
      isMobile,
      selectedVehicle,
      isCenter,
      centerLatitude,
      spotMarker
    });
    */

    if (selectedVehicle) {
      setSelectedMarker(selectedVehicle);

      if (Number(selectedVehicle.Latitude) !== 0 && Number(selectedVehicle.Longitude) !== 0) {
        const timer = setTimeout(() => {
          setMapCenter({
            lat: Number(selectedVehicle.Latitude) !== 0 ? Number(selectedVehicle.Latitude) : centerLatitude,
            lng: Number(selectedVehicle.Longitude ) !== 0 ? Number(selectedVehicle.Longitude) : centerLongitude,
          });
        }, 50);
        return () => clearTimeout(timer);
      }
    } else {
      setSelectedMarker(null);
    }
  }, [locations, selectedVehicle]);
  
  useEffect(() => {
    /*
    console.log("GoogleMap_New.js: useEffect: locations:", {
      isCluster,
      locations,
      isMobile,
      selectedVehicle,
      isCenter,
      centerLatitude,
      spotMarker
    });
    */

    (() => {
        
      if (isCenter || selectedVehicle) {
        // Reset the user interaction flag when explicitly told to center
        userHasMovedMapRef.current = false;
      }
  
      if (selectedVehicle) {
        setMapCenter({
          lat: Number(parseFloat(selectedVehicle?.Latitude) !== 0 ? parseFloat(selectedVehicle?.Latitude) : centerLatitude), 
          lng: Number(parseFloat(selectedVehicle?.Longitude) !== 0 ? parseFloat(selectedVehicle?.Longitude) : centerLongitude),
        });
      } else if (locations?.length && isCenter) {
        setMapCenter({
          lat: Number(parseFloat(locations[0]?.Latitude !== 0 ? parseFloat(locations[0]?.Latitude) : centerLatitude)),
          lng: Number(parseFloat(locations[0]?.Longitude !== 0 ? parseFloat(locations[0]?.Longitude) : centerLongitude)),
        });
      }
      // If the user doesn't move the map, then this code will
      // cause the map to move for a 1-2 seconds whenever the
      // location data is refreshed. Its rather annoying for the
      // user. So dont enable this. 
      else {
        setMapCenter({
          lat: centerLatitude,
          lng: centerLongitude,
        });
      }

      // Calculate bounds if locations are already available
      if (!selectedVehicle && locations && locations.length > 0 && googleMapRef.current) {

        if (!userHasMovedMapRef.current || isCenter) {
          const bounds = new window.google.maps.LatLngBounds();
            
          // Add each valid location to bounds
          locations.forEach(location => {
            const lat = parseFloat(location?.Latitude);
            const lng = parseFloat(location?.Longitude);
            if (lat !== 0 && lng !== 0) {
              bounds.extend({ lat, lng });
            }
          });
            
          // Only fit bounds if we have valid locations
          if (!bounds.isEmpty()) {
            // map.fitBounds(bounds);
            debouncedFitBounds(googleMapRef.current, bounds);
    
            // Store bounds for later use
            boundsRef.current = bounds;
          } else {
            // Fallback to centroid if no valid bounds
            const centroid = getCentroid(locations);
            if (centroid) {
              googleMapRef.current.setCenter(centroid);
            }
          }
        }
      } else if (!selectedVehicle && googleMapRef.current){
        // If no locations, center the map to Auckland by default
        googleMapRef.current.setCenter({ lat: -36.8597274, lng: 174.5414624 });
      }

    })();
  }, [locations, selectedVehicle, isCenter, centerLatitude, spotMarker]);
  
  const displayGeoBound = useMemo(() => {
    return (
      geoBounds &&
        geoBounds.map((geoBound) => {
          if (Number(geoBound.GeoBoundaryTypeId) === 1) {
            return (
              <DisplayPolygon key={geoBound.GeoBoundaryId} geoBound={geoBound} />
            );
          } else {
            return (
              <DisplayCircle key={geoBound.GeoBoundaryId} geoBound={geoBound} />
            );
          }
        })
    );
  }, [geoBounds]);
  
  const displayPolyline = useMemo(() => {
    //console.log("GoogleMap.js.displayPolyline");
    /*
    console.log("GoogleMap_New.js: displayPolyline: locations:", {
      isCluster,
      locations,
      isMobile,
      isPolyline
    });
    */

    if (!locations || !isPolyline) return null;
    
    // Don't process if no significant locations
    if (locations.length < 2) return null;
      
    // Calculate a dynamic simplification tolerance based on zoom
    const currentZoom = googleMapRef.current?.getZoom() || 10;
    const zoomBasedTolerance = 0.0001 * Math.pow(2, 14 - currentZoom);
      
    // Simplify points for the polyline
    const simplifiedLocations = locations.length > 100 
      ? simplifyPath(locations.map(loc => ({
        lat: Number(loc.Latitude),
        lng: Number(loc.Longitude)
      })), zoomBasedTolerance)
      : locations.map(loc => ({
        lat: Number(loc.Latitude),
        lng: Number(loc.Longitude)
      }));
      
    return (
      <DisplayPolyline 
        locations={simplifiedLocations} 
        Highlight={true} 
        options={{
          strokeColor: "#FF0000",
          strokeOpacity: 0.8,
          strokeWeight: 2,
          clickable: false,  // Disable click handlers to save resources
          geodesic: false    // Non-geodesic lines are faster to render
        }}
      />
    );
  }, [locations, isPolyline]);
  
  const setHistory = (item) => {
    //console.log('LocationMap.setHistory.item', item);
    selectedHistory(item);
  };
  
  // Add this function to determine significant changes
  const _checkForSignificantChanges = (newPositions, oldPositions) => {
    // If no previous positions or count changed, consider it significant
    if (!oldPositions.length || newPositions.length !== oldPositions.length) {
      return true;
    }
      
    // Define what "significant" means (e.g., moved more than 100 meters)
    const SIGNIFICANT_DISTANCE = 0.001; // roughly 100 meters in decimal degrees
      
    // Check if any vehicle moved significantly
    for (let i = 0; i < newPositions.length; i++) {
      const newPos = newPositions[i];
      const oldPos = oldPositions.find(p => p.VehicleID === newPos.VehicleID);
        
      if (!oldPos) return true; // New vehicle added
        
      const latDiff = Math.abs(Number(newPos.Latitude) - Number(oldPos.Latitude));
      const lngDiff = Math.abs(Number(newPos.Longitude) - Number(oldPos.Longitude));
        
      if (latDiff > SIGNIFICANT_DISTANCE || lngDiff > SIGNIFICANT_DISTANCE) {
        return true;
      }
    }
      
    return false;
  };
  
    
  // Modify the useEffect that updates map center
  // This is needed so that if the user moves the map,
  // we don't reset the center when refreshing the map
  // for new data
  useEffect(() => {
    const _filteredLocations = locations?.filter((location) => {
      if (
        parseFloat(location?.Latitude) !== 0 &&
          parseFloat(location?.Longitude) !== 0
      )
        return location;
    });
  }, [locations]);
  
  const getCentroid = (locations) => {
    let latSum = 0;
    let lngSum = 0;
  
    locations.forEach((location) => {
      latSum += parseFloat(location.Latitude);
      lngSum += parseFloat(location.Longitude);
    });
  
    return {
      lat: latSum / locations.length,
      lng: lngSum / locations.length,
    };
  };
  
  useEffect(() => {
    const getGeoLocations = async () => {
      try {
        let isMore = true;
        let resData = [];
        let geoLocationItems = [];
        let pageNo = 1;
  
        const existingGeoItems = JSON.parse(
          localStorage.getItem("geolocations")
        );
        if (
          existingGeoItems &&
            existingGeoItems.tenant_id === authTokens.tenant_id &&
            existingGeoItems.expires_in > Date.now() / 1000
        ) {
          setGeoBounds(existingGeoItems.items);
        } else {
          while (isMore) {
            resData = await LocationsApi.getGeoLocations(pageNo, 500);
            geoLocationItems = geoLocationItems.concat(resData.Data);
  
            setGeoBounds(geoLocationItems);
  
            if (pageNo >= Number(resData.Paging.TotalNumberOfPages)) {
              isMore = false;
            }
            pageNo++;
          }
  
          localStorage.setItem(
            "geolocations",
            JSON.stringify({
              tenant_id: authTokens.tenant_id,
              expires_in: Date.now() / 1000 + 14440,
              items: geoLocationItems,
            })
          );
        }
      } catch (err) {
        console.log("err", err);
      }
    };
  
    if (!isDrawing) {
      getGeoLocations();
    }
  }, [authTokens, isDrawing]);
  
  useEffect(() => {
    if (isDrawing) {
      setGeoBounds(drawedGeoBoundary);
    }
  }, [drawedGeoBoundary, isDrawing]);
  
  const selectMarker = useCallback((item) => {
    //console.log('selectMarker', item);
    setSelectedMarker(item);
  }, []);
  
  const unSelectMarker = () => {
    //console.log('unSelectMarker')
    setSelectedMarker(null);
    if (unSelectHistory) unSelectHistory();
  };
    
  const toggleTraffic = () => {
    setisTrafficLayer(!isTrafficLayer);
  };
  
  // const toggleMarkerLabel = () => {
  //   setIsVehicleLabel(!isVehicleLabel);
  //   setMapKey(moment());
  // };
  
  const drawGeoBoundary = (e) => {
    e.preventDefault();
  
    setIsDrawingMode(!isDrawingMode);
    drawingCompleted(null);
  };
  
  const clearGeoBoundary = (e) => {
    e.preventDefault();
  
    if (!isDrawingMode) {
      setIsDrawingMode(false);
      drawingCompleted(null);
    }
  };
  
  const finishGeoBoundary = (e) => {
    setIsDrawingMode(false);
    drawingCompleted(e);
  };
  
  const selectCluster = useCallback((item) => {
    setMapCenter({
      lat: Number(item?.center?.lat()),
      lng: Number(item?.center?.lng()),
    });
  }, []);
  
  const displayMarkers = useMemo(() => {
    /*
    console.log("GoogleMap_New.js: useEffect: locations:", {
      isCluster,
      locations,
      isMobile,
      selectMarker,
      selectCluster,
      isVehicleLabel
    });
    */

    if (isCluster) {
      return (
        <DisplayMarkerCluster
          locations={locations}
          selectMarker={selectMarker}
          selectCluster={selectCluster}
          showMarkerLabel={isVehicleLabel}
        />
      );
    } else {
      return (
        locations &&
                locations?.map((location, i) => {
                  return (
                    <DisplayMarkerWithLabel
                      key={i}
                      location={location}
                      selectMarker={selectMarker}
                      showMarkerLabel={isVehicleLabel}
                      polyLineMarker={true}
                    />
                  );
                })
      );
    }
  }, [isCluster, locations, selectMarker, selectCluster, isVehicleLabel]);
    
  const mapOptions = () => {
    if (isMobile) {
      return {
        gestureHandling: "greedy",
        zoomControl: false,
        mapTypeControlOptions: { position: googleMapPosition.TOP_RIGHT },
        streetViewControlOptions: { position: googleMapPosition.RIGHT_TOP },
      };
    } else {
      return {
        gestureHandling: "greedy",
        zoomControlOptions: { position: googleMapPosition.RIGHT_TOP },
        streetViewControlOptions: { position: googleMapPosition.RIGHT_TOP },
        mapTypeControlOptions: { position: googleMapPosition.TOP_RIGHT },
        streetViewControl: true,
        fullscreenControl: true,
      };
    }
  };
      
  const onMapLoad = useCallback((map) => {
      
    /*
    console.log("GoogleMap_New.js: onMapLoad:", {
      locations,
      isMobile
    });
    */

    if (!googleMapRef.current) {
      googleMapRef.current = map;      
  
      // Initial zoom value from props or default
      map.setZoom(zoom || 7);
      userZoomRef.current = zoom || 7;
  
      // Calculate bounds if locations are already available
      if (locations && locations.length > 0) {
        const bounds = new window.google.maps.LatLngBounds();
          
        // Add each valid location to bounds
        locations.forEach(location => {
          const lat = parseFloat(location?.Latitude);
          const lng = parseFloat(location?.Longitude);
          if (lat !== 0 && lng !== 0) {
            bounds.extend({ lat, lng });
          }
        });
          
        // Only fit bounds if we have valid locations
        if (!bounds.isEmpty()) {
          // map.fitBounds(bounds);
          debouncedFitBounds(map, bounds);
  
          // Store bounds for later use
          boundsRef.current = bounds;
        } else {
          // Fallback to centroid if no valid bounds
          const centroid = getCentroid(locations);
          if (centroid) {
            map.setCenter(centroid);
          }
        }
      } else {
        // If no locations, center the map to Auckland by default
        map.setCenter({ lat: 36.8485, lng: 174.7633 });
      }
  
      // Add listeners to detect user interaction
      // This is needed so that if the user moves the map,
      // we don't reset the center when refreshing the map
      // for new data
      map.addListener('dragstart', () => {
        userHasMovedMapRef.current = true;
      });
      map.addListener('zoom_changed', () => {
        userHasMovedMapRef.current = true;
      });
    }
  }, []);
    
  const memoizedCenter = useMemo(() => mapCenter, [mapCenter]);
  
  return (
    <>
      {isLoaded && (
        <GoogleMap
          // Removed dynamic key to avoid unmounting/re-initializing the map on every update
          // key={mapKey}
          center={memoizedCenter}
          mapContainerStyle={{
            height: "100%",
            width: "100%",
            position: "absolute",
          }}
          zoom={zoom}
          onLoad={onMapLoad} // onMapLoad should initialize the map instance only once
          onZoomChanged={handleZoomChanged}
          options={mapOptions()}
        >
          {displayGeoBound}
          {displayPolyline}
          {isMarker && displayMarkers}
  
          {selectedMarker && (
            <DisplayInfoWindow
              location={selectedMarker}
              unSelectMarker={unSelectMarker}
              selectedHistory={setHistory}
              onSelectedHistory={e => selectedHistory(e)}
              mapCenter={mapCenter}
            />
          )}
    
          {spotMarker && (
            <SpotMarker lat={spotMarker.lat} lng={spotMarker.lng} />
          )}
          <CustomMapControl position={googleMapPosition.TOP_RIGHT}>
            <div style={{ float: "left", position: "relative" }}>
              <button
                className={`googlemap-control ${isTrafficLayer ? "on" : ""}`}
                onClick={toggleTraffic}
              >
                  Traffic
              </button>
            </div>
            {/* Uncomment if using marker labels
              {isUseMarkerLabel && (
                <div style={{ float: "left", position: "relative" }}>
                  <button
                    className={`googlemap-control ${isVehicleLabel ? "on" : ""}`}
                    onClick={toggleMarkerLabel}
                  >
                    Label
                  </button>
                </div>
              )} */}
          </CustomMapControl>
          {isDrawing && (
            <>
              <DrawingGeoBoundary
                isDrawingMode={isDrawingMode}
                drawingCompleted={finishGeoBoundary}
              />
              <CustomMapControl position={googleMapPosition.TOP_LEFT}>
                <div
                  style={{
                    float: "left",
                    position: "relative",
                    fontWeight: "bold",
                  }}
                >
                  <button
                    className="googlemap-control"
                    onClick={(e) => drawGeoBoundary(e)}
                  >
                    <span style={{ color: isDrawingMode ? "#a576fb" : "" }}>
                        Draw
                    </span>
                  </button>
                </div>
                <div style={{ float: "left", position: "relative" }}>
                  <button
                    className="googlemap-control"
                    onClick={(e) => clearGeoBoundary(e)}
                  >
                      Clear
                  </button>
                </div>
              </CustomMapControl>
            </>
          )}
          {isTrafficLayer && <TrafficLayerF autoUpdate />}
          {tripHistory && <TripIconAnimation tripPath={tripHistory} />}
        </GoogleMap>
      )}
    </>
  );
};
  
export default memo(LocationMap);
  